// Shared Supabase client — the single source of truth for the whole app.
// TypeScript source — supabase.js is a thin re-export stub for existing `.js`
// import paths.
//
// Typing strategy (see docs/data-layer-plan.md, Part C): the client is typed with
// the generated `Database` schema (src/types/database.generated.ts, produced by
// `npm run gen:types`), so every .from(...)/.rpc(...) is type-checked.
//
// Creation (see docs/supabase-client-singleton-plan.md, item 3.3): the client is
// created HERE — once, lazily on the first getSupabaseClient() call, then memoized.
// So the getter never returns null and creation no longer depends on a React effect
// firing. There is no setter: nothing outside this module creates the client.
//
// getTypedClient() is kept as a thin alias so the src/data/ repos compile
// unchanged; it can be retired in a later mechanical pass
// (s/getTypedClient/getSupabaseClient/).

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../types/database.generated";

export type TypedSupabaseClient = SupabaseClient<Database>;

var _client: TypedSupabaseClient | null = null;

// The one Supabase client. Created + memoized on first call from the Vite env
// vars; throws fast if they're missing rather than handing back a broken client.
export function getSupabaseClient(): TypedSupabaseClient {
  if (!_client) {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !key) {
      throw new Error(
        "Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file or Vercel project settings."
      );
    }
    _client = createClient<Database>(url, key);
  }
  return _client;
}

// Deprecated alias — getSupabaseClient is the typed client; this returns the same
// instance. Retained so the Part B repos keep compiling.
export function getTypedClient(): TypedSupabaseClient {
  return getSupabaseClient();
}
